import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getOrgOutfits, saveOutfitItem, deleteOutfitItem } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import {
  PencilSquareIcon,
  TrashIcon,
  PhotoIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const RIDER_CATEGORIES = [
  { key: 'helmet', label: 'Helmet / Hat' },
  { key: 'top', label: 'Jacket / Top' },
  { key: 'breeches', label: 'Breeches' },
  { key: 'gloves', label: 'Gloves' },
  { key: 'boots', label: 'Boots' },
  { key: 'accessories', label: 'Accessories' },
];

const HORSE_CATEGORIES = [
  { key: 'bridle', label: 'Bridle' },
  { key: 'saddle', label: 'Saddle' },
  { key: 'saddle_pad', label: 'Saddle Pad' },
  { key: 'wraps', label: 'Wraps / Bandages' },
  { key: 'tail_bow', label: 'Tail Bow' },
  { key: 'bridle_bow', label: 'Bridle Bow' },
  { key: 'horseshoes', label: 'Horseshoes' },
];

export default function OutfitPage() {
  const { orgId } = useParams();
  const { user, organizations } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('rider');
  const [editModal, setEditModal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', image: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const org = organizations.find(o => o._id === orgId);
  const canEdit = ['admin', 'owner', 'leader'].includes(user?.role) || !!user?.permissions?.manageOutfit;
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) loadOutfits();
    else setLoading(false);
  }, [orgId, isAdmin]);

  const loadOutfits = async () => {
    try {
      const res = await getOrgOutfits(orgId);
      setItems(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load outfits');
    } finally {
      setLoading(false);
    }
  };

  const getItem = (type, category) => items.find(i => i.type === type && i.category === category);

  const openEdit = (type, category) => {
    const existing = getItem(type, category);
    setForm({
      name: existing?.name || '',
      description: existing?.description || '',
      image: existing?.image || ''
    });
    setEditModal({ type, category });
    setError('');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2000000) {
      setError('Obraz musi być mniejszy niż 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setForm(prev => ({ ...prev, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!editModal) return;
    setSaving(true);
    setError('');
    try {
      await saveOutfitItem(orgId, {
        type: editModal.type,
        category: editModal.category,
        name: form.name,
        description: form.description,
        image: form.image
      });
      await loadOutfits();
      setEditModal(null);
      setSuccess('Zapisano!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się zapisać');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Usunąć ten element?')) return;
    try {
      await deleteOutfitItem(item._id);
      await loadOutfits();
      setSuccess('Usunięto!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się usunąć');
    }
  };

  const categories = tab === 'rider' ? RIDER_CATEGORIES : HORSE_CATEGORIES;
  const editLabel = editModal
    ? (tab === 'rider' ? RIDER_CATEGORIES : HORSE_CATEGORIES).find(c => c.key === editModal.category)?.label || ''
    : '';

  if (loading) return <Loading />;

  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
        <p>Access restricted to administrators only.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Outfit</h2>
          <div className="subtitle">{org?.name || 'Organization'} — Equipment List</div>
        </div>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && !editModal && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          className={`btn ${tab === 'rider' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('rider')}
        >
          <UserIcon className="icon" /> Rider
        </button>
        <button
          className={`btn ${tab === 'horse' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('horse')}
        >
          Horse
        </button>
      </div>

      <div className="outfit-grid">
        {categories.map(cat => {
          const item = getItem(tab, cat.key);
          return (
            <div key={cat.key} className="outfit-card">
              <div className="outfit-card-image">
                {item?.image ? (
                  <img src={item.image} alt={cat.label} />
                ) : (
                  <div className="outfit-card-placeholder">
                    <PhotoIcon style={{ width: 40, height: 40, color: 'var(--text-muted)' }} />
                  </div>
                )}
              </div>
              <div className="outfit-card-body">
                <div className="outfit-card-label">{cat.label}</div>
                {item?.name && <div className="outfit-card-name">{item.name}</div>}
                {item?.description && <div className="outfit-card-desc">{item.description}</div>}
                {!item?.name && !item?.description && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Brak informacji</div>
                )}
              </div>
              {canEdit && (
                <div className="outfit-card-actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => openEdit(tab, cat.key)}
                    title="Edytuj"
                    style={{ color: 'var(--accent)' }}
                  >
                    <PencilSquareIcon className="icon" />
                  </button>
                  {item && (
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDelete(item)}
                      title="Usuń"
                      style={{ color: 'var(--danger)' }}
                    >
                      <TrashIcon className="icon" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={!!editModal}
        onClose={() => setEditModal(null)}
        title={`Edytuj: ${editLabel}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Anuluj</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </>
        }
      >
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        <div className="form-group">
          <label>Zdjęcie</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {form.image ? (
              <img src={form.image} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }} />
            ) : (
              <div style={{ width: 80, height: 80, background: 'var(--bg-input)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
                <PhotoIcon style={{ width: 28, height: 28, color: 'var(--text-muted)' }} />
              </div>
            )}
            <div>
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                <PhotoIcon className="icon" /> Wybierz zdjęcie
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
              </label>
              {form.image && (
                <button className="btn btn-ghost btn-sm" onClick={() => setForm(prev => ({ ...prev, image: '' }))} style={{ color: 'var(--danger)', marginLeft: 4 }}>
                  Usuń
                </button>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Max 2MB</div>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>Nazwa</label>
          <input
            className="form-input"
            value={form.name}
            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="np. Pikeur Premium Pro"
          />
        </div>
        <div className="form-group">
          <label>Opis / Notatki</label>
          <textarea
            className="form-input"
            rows={4}
            value={form.description}
            onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Gdzie kupić, rozmiar, kolor, cena, inne uwagi..."
          />
        </div>
      </Modal>
    </div>
  );
}
